<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    :disabled="readonly"
    offset-overflow
    offset-y
    min-width="auto"
  >
    <template v-slot:activator="{ on, attrs }">
      <i-text-field
        v-model="dateText"
        v-bind="attrs"
        :prepend-inner-icon="mdiIcon"
        outlined
        hide-details="auto"
        dense
        :label="label"
        :class="className"
        :height="height"
        v-on="on"
        :rules="_rules"
        :readonly="!canInput || readonly"
        @keydown="onKeyDown"
        @blur="onBlur"
        ref="text"
        @click:clear="onClear"
        :disabled="$attrs.disabled"
        autocomplete="false"
        :clearable="clearable && !readonly"
      ></i-text-field>
    </template>
    <template v-if="type == 'time'">
      <v-time-picker
        ref="timePicker"
        v-bind="$attrs"
        format="24hr"
        v-model="time"
        v-if="!readonly"
      ></v-time-picker>
      <!-- v-on="$listeners" -->
    </template>
    <template v-else>
      <v-date-picker
        v-bind="$attrs"
        v-model="date"
        :active-picker.sync="activePicker"
        v-if="!readonly"
        :max="_max"
        :min="_min"
        :type="type"
      ></v-date-picker>
      <!-- v-on="$listeners" -->
    </template>
  </v-menu>
</template>

<script>
import { mdiCalendarBlank, mdiAlarm } from '@mdi/js';
import { addTaskQueue } from '@/utils/util';
const getOnlyNumber = (text = '') => text?.replace(/[^0-9]/g, '') || '';

export default {
  name: 'CommonDatePicker',
  props: {
    clearable: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    useMaxCurrentDate: {
      type: Boolean,
      default: false,
    },
    useMinCurrentDate: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    firstStep: {
      type: String,
      default: 'YEAR',
    },
    min: {
      type: [Number, String],
    },
    max: {
      type: [Number, String],
    },
    useIcon: {
      type: Boolean,
      default: true,
    },
    canInput: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'date',
    },
    height: {
      type: String,
    },
    className: {
      type: String,
    },
  },
  watch: {
    menu(val) {
      val &&
        addTaskQueue(() => {
          if (this.type == 'time') {
            this.$refs.timePicker.selecting = 1;
            this.$refs.timePicker.inputMinute = null;

            return;
          }
          this.activePicker = this.firstStep;
        });
    },

    date(v) {
      this.menu = false;
      if (this.type == 'time') return;
      this.dateText = v.replace(/-/g, '.');
    },
    time(v) {
      if (this.type != 'time') return;
      const [hh, mm] = v?.split(':') || [];
      if (!hh || !mm) this.dateText = '';
      else this.dateText = `${hh}:${mm}`;
    },
    min(v) {
      if (v && this.formatrule(this.date) === true) {
        const flag = this.toDate(v).join('') < this.toDate(this.date).join('');

        if (!flag) {
          this.date = undefined;
          this.dateText = '';
        }
      }
    },
  },
  inject: {
    form: {
      default: null,
    },
  },
  data() {
    return {
      menu: false,
      dateText: null,
      activePicker: null,
    };
  },
  created() {
    if (!this.value) return;
    if (this.type == 'time') {
      const [hh, mm] = this.value?.split(':') || [];
      if (hh && mm) this.dateText = `${hh.padStart(2, 0)}:${mm.padStart(2, 0)}`;
    } else this.dateText = this.toDate(this.value).join('.');
  },
  mounted() {
    this.$refs.text.resetValidation();
    this.form?.register?.(this.$refs.text);
  },
  methods: {
    onClear() {
      this.$refs.text.blur();
      this.$emit('change', '');
      this.$emit('input', '');
      this.$refs.text.resetValidation();
    },
    click() {
      this.menu = true;
    },
    onKeyDown() {
      this.menu = false;
    },
    onBlur() {
      if (this.type == 'time') {
        this.time = !this._rules.every(rule => rule(this.dateText) === true)
          ? ''
          : this.dateText;
        return;
      }

      if (!this._rules.every(rule => rule(this.dateText) === true)) {
        this.dateText = '';
        this.date = '';
        return;
      }

      if (
        this.min &&
        !(this.toDate(this.min).join('') <= this.toDate(this.dateText).join(''))
      ) {
        this.dateText = '';
        this.date = '';
        return;
      }
      if (
        this._max &&
        this.toDate(this._max).join('') < this.toDate(this.dateText).join('')
      ) {
        this.dateText = '';
        this.date = '';
        return;
      }
      this.date = this.toDate(this.dateText).join('-');
    },
    toDate(dateText = '') {
      const onlyNumber = getOnlyNumber(dateText);
      return [
        onlyNumber.slice(0, 4),
        onlyNumber.slice(4, 6),
        onlyNumber.slice(6),
      ].filter(v => v);
    },
    formatrule(v) {
      const type = this.type == 'month' ? 'YYYYMM' : 'YYYYMMDD';
      if (!v) return true; // required rule 은 개별적으로
      const date = this.toDate(v);

      if (!date[2]) date.push('01');
      const format = /^(19[0-9][0-9]|20\d{2})(0[0-9]|1[0-2])(0[1-9]|[1-2][0-9]|3[0-1])$/;
      const flag =
        (v.replace(/[^0-9]/g, '').length == type.length &&
          format.test(date.join(''))) ||
        `${type}` + this.$t(' 형식으로 입력 바랍니다.');
      return flag;
    },
  },
  computed: {
    readonly() {
      if (this.$attrs.hasOwnProperty('readonly')) return this.$attrs.readonly;
      else if (this.form) return this.form.readonly;
      else return false;
    },
    _min() {
      return this.min ? this.min.replaceAll(/[./]/g, '-') : undefined;
    },
    date: {
      get() {
        return this.value?.replace(/[./]/g, '-');
      },
      set(v) {
        if (this.value == v || this.type == 'time') return;
        this.$emit('input', v || '');
        v &&
          getOnlyNumber(v) != getOnlyNumber(this.value) &&
          this.$emit('change', v || '');
      },
    },
    time: {
      get() {
        return this.value;
      },
      set(v) {
        if (this.value == v || this.type != 'time') return;
        const [hh, mm] = v?.split(':') || [];
        const time =
          !hh || !mm ? '' : `${hh.padStart(2, 0)}:${mm.padStart(2, 0)}`;
        this.$emit('input', time);
        v && this.$emit('change', time);
      },
    },
    mdiIcon() {
      return !this.useIcon
        ? null
        : this.type == 'time'
        ? mdiAlarm
        : mdiCalendarBlank;
    },
    _max() {
      if (this.max) {
        return this.max;
      }
      if (this.useMaxCurrentDate) {
        return new Date().toISOString().split('T')[0];
      }
      return '2099-12-31';
    },

    _rules() {
      if (this.$attrs.disabled || this.menu) return [() => true];
      const rules = this.$attrs.rules ? [this.$attrs.rules].flat() : [];
      if (this.type != 'time') rules.push(this.formatrule);
      if (this.type == 'time')
        rules.push(
          v =>
            !v ||
            /^(0?[0-9]|1[0-9]|2[0-3]):([0-5][0-9])$/.test(v) ||
            '24시간 기준 hh:mm 형식으로 입력하셔야 합니다.',
        );
      return rules;
    },
  },
};
</script>

<style></style>
